'use client';

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { type Product } from '@/lib/productService';
import { ProductCard } from '@/components/ProductCard';
import { FeaturedProductCard } from '@/components/FeaturedProductCard';
import { FeaturedProductsCarousel } from '@/components/FeaturedProductsCarousel';
import { CatalogHeaderImagesCarousel } from '@/components/catalog-header-images-carousel';
import { ExperienceCatalogBanner } from '@/components/experience/experience-catalog-banner';
import { CategoryBadgesScroller } from '@/components/category-badges-scroller';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import type { ColorGroup } from '@/lib/matchVariantOptions';
import { useCartStore } from '@/lib/cartStore';
import { trackAffiliateClick } from '@/lib/affiliateClickTracking';
import { trackLinkOpened } from '@/lib/linkTracking';
import { resolveCampaignAttribution } from '@/lib/adClickIds';
import { trackStoreVisit } from '@/lib/storeVisitTracking';
import {
  rememberAffiliateAttribution,
  rememberStoreAttribution,
} from '@/lib/attributionTracking';
import { getStoreClosedReason, type StoreClosedReason } from '@/lib/operatingHours';
import { cn } from '@/lib/utils';
import { getPublicStorePathSegment } from '@/lib/storePathSegment';
import { Search, X, AlertCircle, Clock, Loader2, MessageCircle, SlidersHorizontal } from 'lucide-react';

const ITEMS_PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 2000;
/** Mobile keeps 6 in Mais Vendidos (Todos continues from 7th). Desktop shows 5 (Todos from 6th). */
const MAIS_VENDIDOS_LIMIT_MOBILE = 6;
const MAIS_VENDIDOS_LIMIT_DESKTOP = 5;
/** Matches Tailwind `md` — same breakpoint as the Mais Vendidos `md:basis-1/5` layout. */
const MAIS_VENDIDOS_DESKTOP_MQ = '(min-width: 768px)';
const MULTICOLOR_GRADIENT =
  'conic-gradient(#ef4444,#f59e0b,#facc15,#22c55e,#3b82f6,#a855f7,#ef4444)';
const featuredCarouselSlideClass =
  'px-0.5 shrink-0 snap-start w-[var(--carousel-slide-width)] min-w-[var(--carousel-slide-width)] max-w-[var(--carousel-slide-width)]';

export default function CatalogPage() {
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const locale = useLocale(); // Use useLocale() hook for proper locale detection
  const t = useTranslations('catalog');
  const tRoot = useTranslations();
  const tErrors = useTranslations('errors');

  const storeId = params?.storeId as string;
  const localeParam = params?.locale as string;

  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [resolvedStoreId, setResolvedStoreId] = useState<string>('');
  const [publicStoreId, setPublicStoreId] = useState<string>(storeId);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allProducts, setAllProducts] = useState<Product[]>([]); // Store all loaded products
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [preferredProducts, setPreferredProducts] = useState<Product[]>([]);
  // Default false (mobile-safe): avoid duplicating the 6th preferred in Todos before matchMedia runs.
  const [isDesktopMaisVendidos, setIsDesktopMaisVendidos] = useState(false);
  const [storeClosedReason, setStoreClosedReason] = useState<StoreClosedReason | null>(null);
  const isStoreClosed = storeClosedReason !== null;
  const [catalogHeaderImages, setCatalogHeaderImages] = useState<unknown>(null);
  const [experienceEnabled, setExperienceEnabled] = useState(false);
  const [experienceCatalogPromoMediaUrl, setExperienceCatalogPromoMediaUrl] = useState<
    string | null
  >(null);
  const [categoryFetching, setCategoryFetching] = useState(false);
  const [searchFetching, setSearchFetching] = useState(false);
  // Consumer variant filters (opt-in per store; category is always shown).
  const [filterConfig, setFilterConfig] = useState({
    color: false,
    size: false,
    gender: false,
  });
  const [filterOptions, setFilterOptions] = useState<{
    colorGroups: ColorGroup[];
    sizes: string[];
    genders: string[];
  }>({ colorGroups: [], sizes: [], genders: [] });
  const [selectedBaseColors, setSelectedBaseColors] = useState<string[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedGenders, setSelectedGenders] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchContactWhatsAppUrl, setSearchContactWhatsAppUrl] = useState<string | null>(
    null
  );
  const [searchContactWhatsAppFetching, setSearchContactWhatsAppFetching] = useState(false);

  const setStoreIdStore = useCartStore((state) => state.setStoreId);
  const observerTarget = useRef<HTMLDivElement>(null);
  const featuredSectionRef = useRef<HTMLDivElement>(null);
  const moreProductsSectionRef = useRef<HTMLDivElement>(null);
  const prevCategoryFilterRef = useRef<string | null>(null);
  const prevDebouncedSearchRef = useRef<string | null>(null);
  // Active variant-filter query values, read inside productsQuery so every fetch path includes them.
  const filterColorsRef = useRef<string[]>([]);
  const filterSizesRef = useRef<string[]>([]);
  const filterGendersRef = useRef<string[]>([]);
  const prevVariantFilterKeyRef = useRef<string | null>(null);
  const debouncedSearchRef = useRef(debouncedSearchQuery);
  const skipUrlSyncRef = useRef(true);
  debouncedSearchRef.current = debouncedSearchQuery;

  const urlCategory = searchParams.get('category')?.trim() || null;
  const urlSearch =
    searchParams.get('q')?.trim() || searchParams.get('search')?.trim() || '';

  const syncFiltersToUrl = useCallback(
    (category: string | null, search: string) => {
      const url = new URL(window.location.href);
      const currentCategory = url.searchParams.get('category')?.trim() || null;
      const currentSearch =
        url.searchParams.get('q')?.trim() ||
        url.searchParams.get('search')?.trim() ||
        '';
      const nextSearch = search.trim();
      const categoryMatches =
        (currentCategory?.toLowerCase() ?? null) === (category?.toLowerCase() ?? null);
      if (categoryMatches && currentSearch === nextSearch) {
        return;
      }

      if (category) {
        url.searchParams.set('category', category);
      } else {
        url.searchParams.delete('category');
      }
      if (nextSearch) {
        url.searchParams.set('q', nextSearch);
      } else {
        url.searchParams.delete('q');
        url.searchParams.delete('search');
      }
      const query = url.searchParams.toString();
      const next = query ? `${url.pathname}?${query}` : url.pathname;
      router.replace(next, { scroll: false });
    },
    [router]
  );

  const productsQuery = useCallback(
    (page: number, category: string | null, search: string) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(ITEMS_PER_PAGE),
      });
      const q = search.trim();
      // Search reaches the whole store catalog: the active category filter is ignored
      // while the customer is searching (VT-752).
      if (category && !q) params.set('category', category);
      if (q) params.set('q', q);
      if (filterColorsRef.current.length > 0) params.set('colors', filterColorsRef.current.join(','));
      if (filterSizesRef.current.length > 0) params.set('sizes', filterSizesRef.current.join(','));
      if (filterGendersRef.current.length > 0)
        params.set('genders', filterGendersRef.current.join(','));
      return params.toString();
    },
    []
  );

  // Debounce search: API fetch and URL ?q= update only after the user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Desktop (md+): 5 Mais Vendidos; mobile: 6. API still returns/excludes up to 6.
  useEffect(() => {
    const mql = window.matchMedia(MAIS_VENDIDOS_DESKTOP_MQ);
    const update = () => setIsDesktopMaisVendidos(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Welcome-link funnel: when arriving with ?ref=<token>, record the open and remember the
  // reference so a later "add to cart" can be attributed to this conversation.
  // Wait for the resolved store UUID — public URLs use the slug, but Conversation.storeId
  // and cartStore both key by UUID (see POST /api/track/link).
  useEffect(() => {
    if (!resolvedStoreId) return;
    const ref = searchParams.get('ref')?.trim();
    if (ref) {
      trackLinkOpened(resolvedStoreId, ref);
    }
    const { utmSource, utmCampaign } = resolveCampaignAttribution(searchParams);
    const utmAffiliate = searchParams.get('utm_affiliate');
    trackStoreVisit(resolvedStoreId, utmSource, utmCampaign);
    rememberStoreAttribution(resolvedStoreId, utmSource, utmCampaign);
    trackAffiliateClick(resolvedStoreId, utmAffiliate);
    rememberAffiliateAttribution(resolvedStoreId, utmAffiliate);
  }, [resolvedStoreId, searchParams]);

  // Load store data and initial products (respect ?category= and ?q= from the URL)
  useEffect(() => {
    async function loadData() {
      const initialCategory = searchParams.get('category')?.trim() || null;
      const initialSearch =
        searchParams.get('q')?.trim() || searchParams.get('search')?.trim() || '';

      try {
        setLoading(true);
        setError(null);
        setCurrentPage(1);
        setHasMore(true);
        setAllProducts([]);
        setSearchQuery(initialSearch);
        setDebouncedSearchQuery(initialSearch);
        setSelectedCategory(initialCategory);
        prevCategoryFilterRef.current = initialCategory;
        prevDebouncedSearchRef.current = initialSearch;
        skipUrlSyncRef.current = true;

        // Fetch store data from API
        const [storeResponse, experienceResponse] = await Promise.all([
          fetch(`/api/stores/${storeId}`),
          fetch(`/api/stores/${storeId}/experience`),
        ]);
        if (!storeResponse.ok) {
          throw new Error('Failed to load store');
        }
        const store = await storeResponse.json();
        if (experienceResponse.ok) {
          const { data: experienceData } = await experienceResponse.json();
          setExperienceEnabled(
            experienceData?.hasPackageExperience === true &&
              experienceData?.mobileExperienceMode === 'BOTH'
          );
          setExperienceCatalogPromoMediaUrl(
            experienceData?.experienceCatalogPromoMediaUrl ?? null
          );
        } else {
          setExperienceEnabled(false);
          setExperienceCatalogPromoMediaUrl(null);
        }
        setResolvedStoreId(store.id);
        setPublicStoreId(getPublicStorePathSegment(store.id, store.slug));
        setStoreIdStore(store.id);
        setStoreClosedReason(getStoreClosedReason(store));
        setCatalogHeaderImages(store.catalogHeaderImages ?? null);

        // Catalog filters are a fashion-only feature.
        const isFashion = Boolean(store.hasFashionProducts);
        const colorEnabled = isFashion && Boolean(store.catalogFilterColorEnabled);
        const sizeEnabled = isFashion && Boolean(store.catalogFilterSizeEnabled);
        const genderEnabled = isFashion && Boolean(store.catalogFilterGenderEnabled);
        setFilterConfig({ color: colorEnabled, size: sizeEnabled, gender: genderEnabled });
        if (colorEnabled || sizeEnabled || genderEnabled) {
          fetch(`/api/stores/${store.id}/match-variant-options`)
            .then((res) => (res.ok ? res.json() : null))
            .then((json) => {
              if (!json?.data) return;
              setFilterOptions({
                colorGroups: Array.isArray(json.data.colorGroups) ? json.data.colorGroups : [],
                sizes: Array.isArray(json.data.sizes) ? json.data.sizes : [],
                genders: Array.isArray(json.data.genders) ? json.data.genders : [],
              });
            })
            .catch(() => {});
        }

        // Fetch featured, preferred, and first page of regular products in parallel
        const [featuredResponse, preferredResponse, productsResponse] = await Promise.all([
          fetch(`/api/stores/${store.id}/products?featured=true`),
          fetch(`/api/stores/${store.id}/products?preferred=true`),
          fetch(
            `/api/stores/${store.id}/products?${productsQuery(1, initialCategory, initialSearch)}`
          ),
        ]);

        if (featuredResponse.ok) {
          const { featuredProducts: fp } = await featuredResponse.json();
          setFeaturedProducts(fp || []);
        }

        if (preferredResponse.ok) {
          const { preferredProducts: pp } = await preferredResponse.json();
          setPreferredProducts(pp || []);
        }

        if (!productsResponse.ok) {
          throw new Error('Failed to load products');
        }
        const { products: productsData, categories: categoriesData, pagination } = await productsResponse.json();

        // Empty products array is valid (no products found), not an error
        setAllProducts(productsData || []);
        setCategories(categoriesData || []);
        setHasMore(pagination?.hasMore ?? false);
      } catch (error) {
        console.error('Failed to load catalog data:', error);
        setError(t('error'));
      } finally {
        setLoading(false);
      }
    }

    if (storeId) {
      loadData();
    }
    // Intentionally omit searchParams: including it re-runs load on every router.replace()
    // and can loop forever in production (loading skeleton never clears).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when store changes
  }, [storeId, setStoreIdStore, t, productsQuery]);

  // Match URL category to the store's canonical category label (e.g. comida -> Comida)
  useEffect(() => {
    if (loading || categories.length === 0 || !selectedCategory) return;
    const resolved = categories.find(
      (category) => category.toLowerCase() === selectedCategory.toLowerCase()
    );
    if (resolved && resolved !== selectedCategory) {
      prevCategoryFilterRef.current = resolved;
      setSelectedCategory(resolved);
    }
  }, [categories, loading, selectedCategory]);

  // Keep the URL in sync when the user changes filters in the UI
  useEffect(() => {
    if (loading) {
      skipUrlSyncRef.current = true;
      return;
    }
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    syncFiltersToUrl(selectedCategory, debouncedSearchQuery);
  }, [selectedCategory, debouncedSearchQuery, loading, syncFiltersToUrl]);

  // Browser back/forward: apply URL params to local filter state (only when the URL changes)
  useEffect(() => {
    if (loading || !resolvedStoreId) return;

    setSearchQuery(urlSearch);
    setDebouncedSearchQuery(urlSearch);
    prevDebouncedSearchRef.current = urlSearch;

    prevCategoryFilterRef.current = urlCategory;
    setSelectedCategory(urlCategory);
  }, [urlCategory, urlSearch, loading, resolvedStoreId]);

  // When the user picks or clears a category, refetch from the API (pagination is store-wide, not only the current page)
  useEffect(() => {
    if (!resolvedStoreId || loading) return;

    const prev = prevCategoryFilterRef.current;
    if (selectedCategory === null && prev === null) return;
    if (selectedCategory === prev) return;

    let cancelled = false;

    (async () => {
      try {
        setCategoryFetching(true);
        setError(null);
        const productsResponse = await fetch(
          `/api/stores/${resolvedStoreId}/products?${productsQuery(1, selectedCategory, debouncedSearchRef.current)}`
        );
        if (!productsResponse.ok) {
          throw new Error('Failed to load products for category');
        }
        const { products: productsData, pagination } = await productsResponse.json();
        if (cancelled) return;
        setAllProducts(productsData || []);
        setCurrentPage(1);
        setHasMore(pagination?.hasMore ?? false);
        prevCategoryFilterRef.current = selectedCategory;
      } catch (e) {
        console.error('Failed to load products by category:', e);
        if (!cancelled) setError(t('error'));
      } finally {
        if (!cancelled) setCategoryFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedCategory, resolvedStoreId, loading, productsQuery, t]);

  // Variant filters (color/size/gender): keep query refs in sync and refetch from page 1 on change.
  useEffect(() => {
    const rawNamesByBase = new Map(
      filterOptions.colorGroups.map((group) => [group.baseColorId, group.rawNames])
    );
    filterColorsRef.current = selectedBaseColors.flatMap(
      (baseColorId) => rawNamesByBase.get(baseColorId) ?? []
    );
    filterSizesRef.current = selectedSizes;
    filterGendersRef.current = selectedGenders;

    if (!resolvedStoreId || loading) return;

    const key = JSON.stringify([selectedBaseColors, selectedSizes, selectedGenders]);
    if (prevVariantFilterKeyRef.current === null) {
      prevVariantFilterKeyRef.current = key;
      return;
    }
    if (prevVariantFilterKeyRef.current === key) return;
    prevVariantFilterKeyRef.current = key;

    let cancelled = false;
    (async () => {
      try {
        setCategoryFetching(true);
        setError(null);
        const productsResponse = await fetch(
          `/api/stores/${resolvedStoreId}/products?${productsQuery(1, selectedCategory, debouncedSearchRef.current)}`
        );
        if (!productsResponse.ok) {
          throw new Error('Failed to load products for filters');
        }
        const { products: productsData, pagination } = await productsResponse.json();
        if (cancelled) return;
        setAllProducts(productsData || []);
        setCurrentPage(1);
        setHasMore(pagination?.hasMore ?? false);
      } catch (e) {
        console.error('Failed to load products for filters:', e);
        if (!cancelled) setError(t('error'));
      } finally {
        if (!cancelled) setCategoryFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedBaseColors,
    selectedSizes,
    selectedGenders,
    filterOptions,
    resolvedStoreId,
    loading,
    selectedCategory,
    productsQuery,
    t,
  ]);

  // When the search text changes (debounced), refetch from the API so matches are not limited to the first loaded pages
  useEffect(() => {
    if (!resolvedStoreId || loading) return;

    if (prevDebouncedSearchRef.current === null) {
      prevDebouncedSearchRef.current = debouncedSearchQuery;
      return;
    }

    if (prevDebouncedSearchRef.current === debouncedSearchQuery) return;
    prevDebouncedSearchRef.current = debouncedSearchQuery;

    let cancelled = false;

    (async () => {
      try {
        setSearchFetching(true);
        setError(null);
        const productsResponse = await fetch(
          `/api/stores/${resolvedStoreId}/products?${productsQuery(1, selectedCategory, debouncedSearchQuery)}`
        );
        if (!productsResponse.ok) {
          throw new Error('Failed to load products for search');
        }
        const { products: productsData, pagination } = await productsResponse.json();
        if (cancelled) return;
        setAllProducts(productsData || []);
        setCurrentPage(1);
        setHasMore(pagination?.hasMore ?? false);
      } catch (e) {
        console.error('Failed to load products for search:', e);
        if (!cancelled) setError(t('error'));
      } finally {
        if (!cancelled) setSearchFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearchQuery, resolvedStoreId, loading, selectedCategory, productsQuery, t]);

  // Load more products (pagination)
  const loadMoreProducts = useCallback(async () => {
    if (!resolvedStoreId || loadingMore || !hasMore || categoryFetching || searchFetching) return;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      const productsResponse = await fetch(
        `/api/stores/${resolvedStoreId}/products?${productsQuery(nextPage, selectedCategory, debouncedSearchQuery)}`
      );

      if (!productsResponse.ok) {
        throw new Error('Failed to load more products');
      }

      const { products: productsData, pagination } = await productsResponse.json();

      setAllProducts(prev => [...prev, ...(productsData || [])]);
      setCurrentPage(nextPage);
      setHasMore(pagination?.hasMore ?? false);
    } catch (error) {
      console.error('Failed to load more products:', error);
      // Don't set error state for pagination failures, just log it
    } finally {
      setLoadingMore(false);
    }
  }, [
    resolvedStoreId,
    currentPage,
    hasMore,
    loadingMore,
    categoryFetching,
    searchFetching,
    selectedCategory,
    productsQuery,
    debouncedSearchQuery,
  ]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !loadingMore &&
          !loading &&
          !categoryFetching &&
          !searchFetching
        ) {
          loadMoreProducts();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, loading, categoryFetching, searchFetching, loadMoreProducts]);

  // Retry loading data
  const retryLoad = useCallback(() => {
    const initialCategory = searchParams.get('category')?.trim() || null;
    const initialSearch =
      searchParams.get('q')?.trim() || searchParams.get('search')?.trim() || '';

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setCurrentPage(1);
        setHasMore(true);
        setAllProducts([]);
        setSearchQuery(initialSearch);
        setDebouncedSearchQuery(initialSearch);
        setSelectedCategory(initialCategory);
        prevCategoryFilterRef.current = initialCategory;
        prevDebouncedSearchRef.current = initialSearch;
        skipUrlSyncRef.current = true;

        const [storeResponse, experienceResponse] = await Promise.all([
          fetch(`/api/stores/${storeId}`),
          fetch(`/api/stores/${storeId}/experience`),
        ]);
        if (!storeResponse.ok) {
          throw new Error('Failed to load store');
        }
        const store = await storeResponse.json();
        if (experienceResponse.ok) {
          const { data: experienceData } = await experienceResponse.json();
          setExperienceEnabled(
            experienceData?.hasPackageExperience === true &&
              experienceData?.mobileExperienceMode === 'BOTH'
          );
          setExperienceCatalogPromoMediaUrl(
            experienceData?.experienceCatalogPromoMediaUrl ?? null
          );
        } else {
          setExperienceEnabled(false);
          setExperienceCatalogPromoMediaUrl(null);
        }
        setResolvedStoreId(store.id);
        setPublicStoreId(getPublicStorePathSegment(store.id, store.slug));
        setStoreIdStore(store.id);
        setStoreClosedReason(getStoreClosedReason(store));
        setCatalogHeaderImages(store.catalogHeaderImages ?? null);

        // Retry must refetch the same data as the initial load — without featured here, a
        // transient error + "try again" left the Destaques section permanently hidden.
        const [featuredRetryResponse, preferredRetryResponse, productsResponse] = await Promise.all([
          fetch(`/api/stores/${store.id}/products?featured=true`),
          fetch(`/api/stores/${store.id}/products?preferred=true`),
          fetch(
            `/api/stores/${store.id}/products?${productsQuery(1, initialCategory, initialSearch)}`
          ),
        ]);

        if (featuredRetryResponse.ok) {
          const { featuredProducts: fp } = await featuredRetryResponse.json();
          setFeaturedProducts(fp || []);
        }
        if (preferredRetryResponse.ok) {
          const { preferredProducts: pp } = await preferredRetryResponse.json();
          setPreferredProducts(pp || []);
        }
        if (!productsResponse.ok) {
          throw new Error('Failed to load products');
        }
        const { products: productsData, categories: categoriesData, pagination } = await productsResponse.json();

        setAllProducts(productsData || []);
        setCategories(categoriesData || []);
        setHasMore(pagination?.hasMore ?? false);
      } catch (error) {
        console.error('Failed to load catalog data:', error);
        setError(t('error'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [storeId, setStoreIdStore, t, productsQuery, searchParams]);

  const maisVendidosLimit = isDesktopMaisVendidos
    ? MAIS_VENDIDOS_LIMIT_DESKTOP
    : MAIS_VENDIDOS_LIMIT_MOBILE;

  const displayedPreferredProducts = useMemo(
    () => preferredProducts.slice(0, maisVendidosLimit),
    [preferredProducts, maisVendidosLimit]
  );

  // Category and search are applied server-side via /api/stores/.../products; keep only inactive guard.
  // On desktop the API still excludes 6 Mais Vendidos ids; prepend the 6th into Todos so browsing
  // continues from product 6 without a duplicate in the carousel.
  const filteredProducts = useMemo(() => {
    const active = allProducts.filter((p) => p.active !== false);
    if (!isDesktopMaisVendidos || preferredProducts.length <= MAIS_VENDIDOS_LIMIT_DESKTOP) {
      return active;
    }
    // Search already reaches the whole catalog (no preferred exclusion) — do not re-inject.
    if (debouncedSearchQuery.trim()) {
      return active;
    }
    // Variant filters (color/size/gender) are resolved server-side against the variant rows; the
    // preferred list is not filtered by them, so re-injecting here would surface products the
    // shopper explicitly filtered out.
    if (
      selectedBaseColors.length > 0 ||
      selectedSizes.length > 0 ||
      selectedGenders.length > 0
    ) {
      return active;
    }

    const overflow = preferredProducts.slice(MAIS_VENDIDOS_LIMIT_DESKTOP);
    const existingIds = new Set(active.map((p) => p.id));
    const toPrepend = overflow.filter((p) => {
      if (p.active === false || existingIds.has(p.id)) return false;
      if (!selectedCategory) return true;
      return (p.category ?? '').toLowerCase() === selectedCategory.toLowerCase();
    });
    return toPrepend.length > 0 ? [...toPrepend, ...active] : active;
  }, [
    allProducts,
    debouncedSearchQuery,
    isDesktopMaisVendidos,
    preferredProducts,
    selectedCategory,
    selectedBaseColors,
    selectedSizes,
    selectedGenders,
  ]);
  const searchContactQuery = debouncedSearchQuery.trim();
  const hasSearchWithNoResults =
    searchContactQuery.length > 0 && filteredProducts.length === 0;

  useEffect(() => {
    if (
      !resolvedStoreId ||
      loading ||
      error ||
      categoryFetching ||
      searchFetching ||
      !hasSearchWithNoResults
    ) {
      setSearchContactWhatsAppUrl(null);
      setSearchContactWhatsAppFetching(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setSearchContactWhatsAppFetching(true);
        setSearchContactWhatsAppUrl(null);
        const params = new URLSearchParams({
          message: t('searchWhatsAppMessage', { search: searchContactQuery }),
        });
        const response = await fetch(
          `/api/stores/${encodeURIComponent(resolvedStoreId)}/whatsapp-contact?${params.toString()}`
        );
        if (!response.ok) {
          throw new Error('Failed to load store WhatsApp contact');
        }
        const data = (await response.json()) as { whatsAppUrl?: string | null };
        if (!cancelled) {
          setSearchContactWhatsAppUrl(data.whatsAppUrl ?? null);
        }
      } catch (e) {
        console.error('Failed to load store WhatsApp contact:', e);
        if (!cancelled) {
          setSearchContactWhatsAppUrl(null);
        }
      } finally {
        if (!cancelled) {
          setSearchContactWhatsAppFetching(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    categoryFetching,
    error,
    hasSearchWithNoResults,
    loading,
    resolvedStoreId,
    searchContactQuery,
    searchFetching,
    t,
  ]);

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setSelectedCategory(null);
    setSelectedBaseColors([]);
    setSelectedSizes([]);
    setSelectedGenders([]);
    syncFiltersToUrl(null, '');
  };

  const toggleFilterValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    value: string
  ) => {
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const clearSearch = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    syncFiltersToUrl(selectedCategory, '');
  };

  const clearCategory = () => {
    setSelectedCategory(null);
    syncFiltersToUrl(null, debouncedSearchQuery);
  };

  const scrollFromBannerClick = useCallback(() => {
    const target =
      featuredProducts.length > 0
        ? featuredSectionRef.current
        : moreProductsSectionRef.current;
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [featuredProducts.length]);

  const experiencePreviewProducts = useMemo(() => {
    const seen = new Set<string>();
    const merged: Product[] = [];
    for (const product of [...featuredProducts, ...preferredProducts, ...allProducts]) {
      if (seen.has(product.id)) continue;
      seen.add(product.id);
      merged.push(product);
    }
    return merged;
  }, [allProducts, featuredProducts, preferredProducts]);

  const selectedVariantFilterCount =
    selectedBaseColors.length + selectedSizes.length + selectedGenders.length;
  const anyVariantFilterEnabled =
    (filterConfig.color && filterOptions.colorGroups.length > 0) ||
    (filterConfig.size && filterOptions.sizes.length > 0) ||
    (filterConfig.gender && filterOptions.genders.length > 0);
  const hasActiveFilters =
    debouncedSearchQuery || selectedCategory || selectedVariantFilterCount > 0;
  const hasSearchText = searchQuery.trim().length > 0;
  const categoryBadgeClass =
    'h-8 shrink-0 cursor-pointer px-3 text-sm transition-colors';
  const inactiveCategoryBadgeClass = cn(
    categoryBadgeClass,
    'hover:bg-gray-200 dark:hover:bg-gray-700'
  );
  const activeCategoryBadgeClass = cn(
    categoryBadgeClass,
    'hover:bg-primary/85'
  );

  return (
    <div className="space-y-6">
      {/* Store Closed Banner */}
      {!loading && !error && isStoreClosed && (
        <Alert className="border-amber-500/50 bg-amber-500/10">
          <Clock className="h-4 w-4" />
          <AlertTitle>
            {storeClosedReason === 'temporarilyClosed'
              ? t('storeTemporarilyClosed')
              : t('storeClosed')}
          </AlertTitle>
          <AlertDescription>
            {storeClosedReason === 'temporarilyClosed'
              ? t('storeTemporarilyClosedDescription')
              : t('storeClosedDescription')}
          </AlertDescription>
        </Alert>
      )}

      {!loading && !error && (
        <CatalogHeaderImagesCarousel
          images={catalogHeaderImages}
          onBannerClick={scrollFromBannerClick}
        />
      )}

      {!loading && !error && experienceEnabled && publicStoreId && (
        <ExperienceCatalogBanner
          normalizedLocale={localeParam}
          publicStoreId={publicStoreId}
          locale={locale}
          products={experiencePreviewProducts}
          promoMediaUrl={experienceCatalogPromoMediaUrl}
          className="sm:hidden"
        />
      )}

      {!loading && !error && (
        <InputGroup className="bg-secondary text-secondary-foreground">
          <InputGroupAddon>
            <Search className="h-4 w-4" />
          </InputGroupAddon>
          <InputGroupInput
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label={t('searchPlaceholder')}
          />
          {searchQuery ? (
            <InputGroupAddon align="inline-end">
              {searchQuery.trim() !== debouncedSearchQuery.trim() || searchFetching ? (
                <Loader2
                  className="h-4 w-4 animate-spin text-muted-foreground"
                  aria-label={t('loading')}
                />
              ) : (
                <InputGroupButton
                  size="icon-xs"
                  onClick={clearSearch}
                  aria-label={t('clearFilters')}
                >
                  <X className="h-4 w-4" />
                </InputGroupButton>
              )}
            </InputGroupAddon>
          ) : null}
        </InputGroup>
      )}

      {!loading && !error && anyVariantFilterEnabled && (
        <div className="flex items-center gap-2">
          <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <SlidersHorizontal className="h-4 w-4" />
                {t('filters.button')}
                {selectedVariantFilterCount > 0 ? (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                    {selectedVariantFilterCount}
                  </Badge>
                ) : null}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="flex w-full max-w-sm flex-col p-0">
              <SheetHeader className="border-b px-4 py-3">
                <SheetTitle>{t('filters.title')}</SheetTitle>
              </SheetHeader>

              <div className="flex-1 space-y-6 overflow-y-auto px-4 py-4">
                {filterConfig.color && filterOptions.colorGroups.length > 0 ? (
                  <section className="space-y-3">
                    <h3 className="text-sm font-semibold">{t('filters.colorTitle')}</h3>
                    <div className="flex flex-wrap gap-2">
                      {filterOptions.colorGroups.map((group) => {
                        const active = selectedBaseColors.includes(group.baseColorId);
                        return (
                          <button
                            key={group.baseColorId}
                            type="button"
                            aria-pressed={active}
                            onClick={() =>
                              toggleFilterValue(setSelectedBaseColors, group.baseColorId)
                            }
                            className={cn(
                              'flex h-9 shrink-0 items-center gap-2 rounded-full border px-3 text-sm transition-colors',
                              active
                                ? 'border-foreground bg-primary/10'
                                : 'border-border hover:bg-gray-100 dark:hover:bg-gray-800'
                            )}
                          >
                            <span
                              aria-hidden
                              className="h-4 w-4 shrink-0 rounded-full border border-black/15 dark:border-white/20"
                              style={
                                group.baseColorId === 'multicolor'
                                  ? { backgroundImage: MULTICOLOR_GRADIENT }
                                  : { backgroundColor: group.hex }
                              }
                            />
                            <span>{tRoot(group.labelKey)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ) : null}

                {filterConfig.size && filterOptions.sizes.length > 0 ? (
                  <section className="space-y-3">
                    <h3 className="text-sm font-semibold">{t('filters.sizeTitle')}</h3>
                    <div className="flex flex-wrap gap-2">
                      {filterOptions.sizes.map((size) => {
                        const active = selectedSizes.includes(size);
                        return (
                          <Badge
                            key={size}
                            variant={active ? 'default' : 'secondary'}
                            className={cn(
                              'h-8 cursor-pointer px-3 text-sm',
                              !active && 'hover:bg-gray-200 dark:hover:bg-gray-700'
                            )}
                            onClick={() => toggleFilterValue(setSelectedSizes, size)}
                          >
                            {size}
                          </Badge>
                        );
                      })}
                    </div>
                  </section>
                ) : null}

                {filterConfig.gender && filterOptions.genders.length > 0 ? (
                  <section className="space-y-3">
                    <h3 className="text-sm font-semibold">{t('filters.genderTitle')}</h3>
                    <div className="flex flex-wrap gap-2">
                      {filterOptions.genders.map((gender) => {
                        const active = selectedGenders.includes(gender);
                        return (
                          <Badge
                            key={gender}
                            variant={active ? 'default' : 'secondary'}
                            className={cn(
                              'h-8 cursor-pointer px-3 text-sm',
                              !active && 'hover:bg-gray-200 dark:hover:bg-gray-700'
                            )}
                            onClick={() => toggleFilterValue(setSelectedGenders, gender)}
                          >
                            {gender}
                          </Badge>
                        );
                      })}
                    </div>
                  </section>
                ) : null}
              </div>

              <SheetFooter className="flex-row gap-2 border-t px-4 py-3">
                <Button
                  variant="ghost"
                  className="flex-1"
                  onClick={() => {
                    setSelectedBaseColors([]);
                    setSelectedSizes([]);
                    setSelectedGenders([]);
                  }}
                  disabled={selectedVariantFilterCount === 0}
                >
                  {t('clearFilters')}
                </Button>
                <Button className="flex-1" onClick={() => setFiltersOpen(false)}>
                  {t('filters.apply')}
                </Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>
      )}

      {!loading && !error && categories.length > 0 && (
        <CategoryBadgesScroller
          previousLabel={t('carouselPrevious')}
          nextLabel={t('carouselNext')}
        >
          <Badge
            variant={!selectedCategory ? 'default' : 'secondary'}
            className={
              !selectedCategory ? activeCategoryBadgeClass : inactiveCategoryBadgeClass
            }
            onClick={clearCategory}
          >
            {t('allCategories')}
          </Badge>
          {categories.map((category) => (
            <Badge
              key={category}
              variant={selectedCategory === category ? 'default' : 'secondary'}
              className={
                selectedCategory === category
                  ? activeCategoryBadgeClass
                  : inactiveCategoryBadgeClass
              }
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </Badge>
          ))}
        </CategoryBadgesScroller>
      )}

      {/* Featured products carousel — hidden while the customer is searching so results
          show up right below the search box and categories (VT-752) */}
      {!loading && !error && !hasSearchText && featuredProducts.length > 0 && (
        <div ref={featuredSectionRef} className="scroll-mt-4 space-y-3">
          <h2 className="font-semibold">{t('featuredProductsTitle')}</h2>
          <FeaturedProductsCarousel
            previousLabel={t('carouselPrevious')}
            nextLabel={t('carouselNext')}
          >
            {featuredProducts.map((product) => (
              <FeaturedProductCard
                key={product.id}
                product={product}
                storeId={resolvedStoreId}
                publicStoreId={publicStoreId}
                locale={localeParam}
                isStoreClosed={isStoreClosed}
              />
            ))}
          </FeaturedProductsCarousel>
        </div>
      )}

      {/* Mais Vendidos — store-picked products in the same square format as Todos os Produtos.
          Mobile: up to 6 (2 per view, swipe, auto-advance every 3s). Desktop (md+): up to 5
          (5 per view); the 6th continues in Todos os Produtos.
          Hidden while the customer is searching (VT-752). */}
      {!loading && !error && !hasSearchText && displayedPreferredProducts.length > 0 && (
        <div className="scroll-mt-4 space-y-3">
          <h2 className="font-semibold">{t('preferredSectionTitle')}</h2>
          <FeaturedProductsCarousel
            previousLabel={t('carouselPrevious')}
            nextLabel={t('carouselNext')}
            autoPlay="mobile"
            autoPlayIntervalMs={3000}
            autoPlayStep={2}
            itemBasisClassName="basis-1/2 sm:basis-1/3 md:basis-1/5"
          >
            {displayedPreferredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                storeId={resolvedStoreId}
                publicStoreId={publicStoreId}
                locale={localeParam}
                isStoreClosed={isStoreClosed}
              />
            ))}
          </FeaturedProductsCarousel>
        </div>
      )}

      {/* Active Filters and Results Count */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-sm"
          >
            {t('clearFilters')}
          </Button>
          {/* Search ignores the selected category (VT-752) — make that explicit to the customer */}
          {hasSearchText && selectedCategory && (
            <p className="text-xs text-muted-foreground">
              {t('searchIgnoresCategoryHint')}
            </p>
          )}
        </div>
      )}

      {/* Full-page loading (initial) or grid skeleton while switching category (keep grid mounted during search to avoid image remount flash) */}
      {(loading || (!loading && !error && categoryFetching)) && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 md:grid-cols-4 lg:grid-cols-5">
            {Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="aspect-square w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <Alert variant="destructive" className="my-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{error}</AlertTitle>
          <AlertDescription className="mt-2 flex items-center justify-between">
            <span>{tErrors('generic')}</span>
            <Button onClick={retryLoad} variant="outline">
              {tErrors('tryAgain')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Product Grid - 2 per row on mobile, more on desktop; stays mounted while search refetches (stale list + dim) */}
      {!loading && !error && !categoryFetching && filteredProducts.length > 0 && (
        <>
          <div ref={moreProductsSectionRef} className="scroll-mt-4 space-y-3">
            <h2 className="font-semibold">{t('moreProductsTitle')}</h2>
            <div
              className={cn(
                'grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 md:grid-cols-4 lg:grid-cols-5 transition-opacity',
                searchFetching && 'pointer-events-none opacity-60'
              )}
            >
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  storeId={resolvedStoreId}
                  publicStoreId={publicStoreId}
                  locale={localeParam}
                  isStoreClosed={isStoreClosed}
                />
              ))}
            </div>
          </div>

          {/* Infinite scroll: same API pagination for category, all products, and search (q) */}
          <div ref={observerTarget} className="py-4 min-h-px">
            {loadingMore && (
              <div className="flex flex-col items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
                <span>{t('loading')}</span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Search in progress with no rows to show yet (e.g. first query) */}
      {!loading && !error && !categoryFetching && searchFetching && filteredProducts.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
          <p className="text-sm">{t('loading')}</p>
        </div>
      )}

      {/* No Products Found */}
      {!loading && !error && !categoryFetching && !searchFetching && filteredProducts.length === 0 && (
        <div className="text-center py-16 px-4 rounded-2xl border border-card-border bg-card/30">
          <div className="text-5xl mb-4 opacity-80">🔍</div>
          <h3 className="text-xl font-semibold mb-2">{t('noProducts')}</h3>
          <p className="text-muted-foreground text-sm mb-4">
            {hasActiveFilters ? t('clearFiltersHint') : t('noProductsHint')}
          </p>
          <div className="flex flex-col items-center justify-center gap-2 sm:flex-row">
            {hasActiveFilters && (
              <Button onClick={clearFilters} variant="secondary">
                {t('clearFilters')}
              </Button>
            )}
            {hasSearchWithNoResults && searchContactWhatsAppFetching && (
              <Button variant="outline" disabled>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                {t('askStoreAboutProduct')}
              </Button>
            )}
            {hasSearchWithNoResults &&
              !searchContactWhatsAppFetching &&
              searchContactWhatsAppUrl && (
                <Button
                  asChild
                  variant="outline"
                  className="text-green-600 hover:text-green-700"
                >
                  <a
                    href={searchContactWhatsAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="size-4" />
                    {t('askStoreAboutProduct')}
                  </a>
                </Button>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
